/**
 * Moving items to a location, the one way every screen does it: the bulk bar,
 * a drag onto the tree, the sort screen.
 *
 * It goes through `runBulk`, so a move is refused exactly where editing the
 * item's location by hand would be, and a failure names the items it left
 * behind. A move that worked says where things went and offers Undo, which
 * sends each item back to wherever it was before -- not to one place, because
 * a selection from the Items screen can come from ten bins.
 */

import { useCallback, useState } from "react";
import { pathLabel } from "@iltocat/shared";
import { plural } from "./api.js";
import { runBulk, type BulkFailure } from "./bulkItems.js";
import { undoPlan } from "./locationMoves.js";
import type { Taxonomy } from "./useTaxonomy.js";
import { useToast } from "../components/Toast.js";

export interface Mover {
  /** Moves them and answers with the ones that did not move. */
  move: (ids: readonly string[], to: string | null) => Promise<BulkFailure[]>;
  /** Items finished so far, while a move is running; null otherwise. */
  working: number | null;
}

export function useMover({
  taxonomy,
  whereIs,
  onDone,
}: {
  taxonomy: Taxonomy;
  /** Where an item is now, for Undo. `undefined` for one this screen has not loaded. */
  whereIs: (id: string) => string | null | undefined;
  /** Something moved, so the list on screen should be read again. */
  onDone: () => void;
}): Mover {
  const [working, setWorking] = useState<number | null>(null);
  const toast = useToast();

  const move = useCallback(
    async (ids: readonly string[], to: string | null): Promise<BulkFailure[]> => {
      const before = ids.map((id) => ({ id, from: whereIs(id) }));
      setWorking(0);
      const failures = await runBulk(ids, { kind: "location", locationId: to }, setWorking);
      setWorking(null);
      if (to) taxonomy.remember(to, null);
      onDone();
      void taxonomy.reload();

      const failed = new Set(failures.map((failure) => failure.id));
      const moved = before.filter(
        (entry): entry is { id: string; from: string | null } =>
          !failed.has(entry.id) && entry.from !== undefined,
      );
      if (ids.length > failures.length) {
        const node = to ? taxonomy.byId.get(to) : undefined;
        const where = node ? `to ${pathLabel(node.path)}` : "out of every location";
        const plan = undoPlan(moved, to);
        toast(
          `Moved ${plural(ids.length - failures.length, "item")} ${where}.`,
          plan.length > 0
            ? {
                label: "Undo",
                run: () =>
                  void (async () => {
                    for (const step of plan)
                      await runBulk(step.ids, { kind: "location", locationId: step.to }, () => {});
                    onDone();
                    void taxonomy.reload();
                    toast("Put back.");
                  })(),
              }
            : undefined,
        );
      }
      return failures;
    },
    [taxonomy, whereIs, onDone, toast],
  );

  return { move, working };
}
